import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useCart } from './useCart';

/**
 * Hook to automatically load cart data for customer users
 * 
 * Usage:
 * - Add to your main App component or layout
 * - Automatically loads cart once per session for customers
 * - Resets when user logs out or changes
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable auto-loading (default: true)
 * @param {Function} options.onError - Error callback
 */
export const useCartLoader = (options = {}) => {
  const { enabled = true, onError } = options;
  const { user } = useAuth();
  const { fetchCart } = useCart();
  const hasFetchedRef = useRef(false);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    // Skip if disabled or no user
    if (!enabled || !user) {
      hasFetchedRef.current = false;
      lastUserIdRef.current = null;
      return;
    }

    // Only fetch for customer users
    if (user.role !== 'customer') {
      hasFetchedRef.current = false;
      return;
    }

    // Reset flag if user changed
    if (lastUserIdRef.current !== user.id) {
      hasFetchedRef.current = false;
      lastUserIdRef.current = user.id;
    }

    // Fetch cart only once per user session
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      
      fetchCart()
        .then(() => {
          console.log('✅ Cart loaded successfully');
        })
        .catch((error) => {
          console.error('❌ Failed to load cart:', error);
          if (onError) {
            onError(error);
          }
        });
    }
  }, [user, user?.id, user?.role, enabled, fetchCart, onError]);

  return null;
};

export default useCartLoader;